import type { PhotoMetadata } from './extractPhotoMetadata';

export interface StagedPhotoMeta extends PhotoMetadata {
  clientId: string;
  file: File;
}

export interface PhotoCluster {
  id: string;
  photos: StagedPhotoMeta[];
}

export interface ClusterResult {
  clusters: PhotoCluster[];
  unsorted: StagedPhotoMeta[];
}

export interface ClusterOptions {
  timeGapHours?: number;
  gpsDistanceKm?: number;
}

// Boundary between two consecutive (by capture time) photos: a >6h gap, or a
// >2km jump when both have GPS. Missing GPS on either side falls back to
// time-only. Named/exported so these are a config change, not a rewrite —
// see the project plan's "calibrate against a real photo library" step.
export const TIME_GAP_HOURS = 6;
export const GPS_DISTANCE_KM = 2;

export function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Groups photos into candidate events by capture-time proximity (and GPS
 * proximity, when available). Photos with no capture date are never guessed
 * into a cluster — they're returned separately as `unsorted` so a missing
 * EXIF date can't silently corrupt a good cluster's boundary.
 */
export function clusterPhotos(photos: StagedPhotoMeta[], opts: ClusterOptions = {}): ClusterResult {
  const timeGapHours = opts.timeGapHours ?? TIME_GAP_HOURS;
  const gpsDistanceKm = opts.gpsDistanceKm ?? GPS_DISTANCE_KM;

  const unsorted = photos.filter(p => p.takenAt === null);
  const dated = photos
    .filter((p): p is StagedPhotoMeta & { takenAt: Date } => p.takenAt !== null)
    .slice()
    .sort((a, b) => a.takenAt.getTime() - b.takenAt.getTime());

  const clusters: PhotoCluster[] = [];
  let current: StagedPhotoMeta[] = [];

  for (const photo of dated) {
    if (current.length === 0) {
      current.push(photo);
      continue;
    }
    const prev = current[current.length - 1];
    const gapHours = (photo.takenAt!.getTime() - prev.takenAt!.getTime()) / (1000 * 60 * 60);
    let boundary = gapHours > timeGapHours;
    if (!boundary && prev.gps && photo.gps) {
      boundary = haversineKm(prev.gps, photo.gps) > gpsDistanceKm;
    }
    if (boundary) {
      clusters.push({ id: `cluster-${current[0].clientId}`, photos: current });
      current = [photo];
    } else {
      current.push(photo);
    }
  }
  if (current.length > 0) {
    clusters.push({ id: `cluster-${current[0].clientId}`, photos: current });
  }

  return { clusters, unsorted };
}
